import { SettingCategory } from './Setting';
import { CATEGORY_ALIASES, SETTING_ALIASES } from './settingsSearchAliases';

/**
 * The searchable and groupable facts about one settings row, kept separate from the rendered
 * row itself. Filtering used to read these back off already-constructed JSX
 * (`element.props.data.category`), which meant the whole settings tree had to be built before
 * anything could be counted or grouped — and made sub-row detection depend on Aphrodite
 * style-object identity.
 */
export interface ISettingSearchTarget {
	category: SettingCategory;
	/** The label as plain text, when it has one. Some rows render a button or node instead. */
	labelText?: string;
	info?: string;
	/**
	 * Dependent rows render indented beneath a parent. They stay visible when the parent matches
	 * a search, so a matched parent never appears with its children missing.
	 */
	isSubRow?: boolean;
}

/** Below this a token is matched exactly, so `AI` reaches the AI settings and not "Em-ai-l". */
const MIN_PREFIX_LENGTH = 3;
/** Distance-1 on a short token collides too readily (`card`/`care`, `send`/`sand`). */
const MIN_FUZZY_LENGTH = 5;

const normalize = (value: string | undefined) => value?.trim().toLowerCase() ?? '';

/**
 * Enough of a stemmer to make an admin's wording and the product's wording meet: plurals so
 * `policy` and `policies` are one word, and `-ing`/`-ed` so `open tracking` reaches
 * `Track Opened Emails`.
 */
function stem(token: string): string {
	if (token.length > 4 && token.endsWith('ies')) {
		return `${token.slice(0, -3)}y`;
	}
	if (token.length > 4 && /(?:ss|x|z|ch|sh)es$/.test(token)) {
		return token.slice(0, -2);
	}
	if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) {
		return token.slice(0, -1);
	}
	if (token.length > 5 && token.endsWith('ing')) {
		return token.slice(0, -3);
	}
	if (token.length > 4 && token.endsWith('ed')) {
		return token.slice(0, -2);
	}
	return token;
}

const tokenize = (value: string): string[] =>
	value
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(Boolean)
		.map(stem);

/**
 * Damerau–Levenshtein distance of at most one — substitution, insertion, deletion, or a
 * transposition of adjacent characters, which is what `complaince` for `compliance` is.
 */
function isWithinOneEdit(a: string, b: string): boolean {
	if (a === b) {
		return true;
	}
	if (Math.abs(a.length - b.length) > 1) {
		return false;
	}
	if (a.length === b.length) {
		let mismatch = -1;
		for (let i = 0; i < a.length; i++) {
			if (a[i] === b[i]) {
				continue;
			}
			if (mismatch >= 0) {
				return mismatch === i - 1 && a[mismatch] === b[i] && a[i] === b[mismatch] && a.slice(i + 1) === b.slice(i + 1);
			}
			mismatch = i;
		}
		return true;
	}
	const shorter = a.length < b.length ? a : b;
	const longer = a.length < b.length ? b : a;
	let i = 0;
	while (i < shorter.length && shorter[i] === longer[i]) {
		i++;
	}
	return shorter.slice(i) === longer.slice(i + 1);
}

/**
 * Tiered so that looseness is earned by length: an exact stem always counts, a prefix lets a
 * half-typed word match, and only a token long enough to survive it gets typo tolerance. The
 * first character must agree — typos land on later characters far more often than the first.
 */
function tokenMatches(queryToken: string, targetTokens: Set<string>): boolean {
	if (targetTokens.has(queryToken)) {
		return true;
	}
	for (const targetToken of targetTokens) {
		if (queryToken.length >= MIN_PREFIX_LENGTH && targetToken.startsWith(queryToken)) {
			return true;
		}
		if (
			queryToken.length >= MIN_FUZZY_LENGTH &&
			queryToken[0] === targetToken[0] &&
			isWithinOneEdit(queryToken, targetToken)
		) {
			return true;
		}
	}
	return false;
}

/**
 * Keyed by the text rather than the row, because rows are rebuilt on every render while the
 * strings they carry are fixed. Bounded by the number of distinct settings, so it never evicts.
 */
const targetTokenCache = new Map<string, Set<string>>();

function getTargetTokens(target: ISettingSearchTarget): Set<string> {
	const cacheKey = `${target.labelText ?? ''}|${target.info ?? ''}|${target.category}`;
	const cached = targetTokenCache.get(cacheKey);
	if (cached) {
		return cached;
	}
	const aliases = [...(SETTING_ALIASES[target.labelText ?? ''] ?? []), ...(CATEGORY_ALIASES[target.category] ?? [])];
	const tokens = new Set([
		...tokenize(target.labelText ?? ''),
		...tokenize(target.info ?? ''),
		...tokenize(target.category ?? ''),
		...aliases.flatMap(alias => tokenize(alias)),
	]);
	targetTokenCache.set(cacheKey, tokens);
	return tokens;
}

/**
 * Every word of the query has to land somewhere in the row's label, help text, group name or
 * aliases — in any order. Matching whole words rather than a raw substring is what stops a
 * two-letter query matching the middle of every longer word.
 */
export function settingMatchesQuery(target: ISettingSearchTarget, query: string): boolean {
	const needle = normalize(query);
	if (!needle) {
		return true;
	}
	const queryTokens = tokenize(needle);
	// A query of nothing but punctuation is a slip of the keyboard, not a request for everything.
	if (queryTokens.length === 0) {
		return false;
	}
	const targetTokens = getTargetTokens(target);
	return queryTokens.every(queryToken => tokenMatches(queryToken, targetTokens));
}

/**
 * The nearest preceding row that is not itself a sub-row — i.e. the parent whose match should
 * keep this dependent row visible.
 */
function findParent<T extends ISettingSearchTarget>(targets: T[], index: number): T | undefined {
	// Bounded by the row's own group: a parent in another category does not govern it, so its
	// matching must not keep this row visible.
	const { category } = targets[index];
	for (let i = index - 1; i >= 0; i--) {
		if (targets[i].category !== category) {
			return undefined;
		}
		if (!targets[i].isSubRow) {
			return targets[i];
		}
	}
	return undefined;
}

export interface IFilterSettingsOptions {
	/** Restrict to one group. Omit to search across every group. */
	category?: SettingCategory;
	query?: string;
}

/**
 * Filter settings rows by group and search text, operating on data rather than rendered output.
 *
 * Ordering is preserved, and the input array must stay in render order so a dependent row can
 * still find its parent.
 */
export function filterSettings<T extends ISettingSearchTarget>(
	targets: T[],
	options: IFilterSettingsOptions = {}
): T[] {
	const { category, query } = options;
	const needle = normalize(query);

	return targets.filter((target, index) => {
		if (category !== undefined && target.category !== category) {
			return false;
		}
		if (!needle) {
			return true;
		}
		if (settingMatchesQuery(target, needle)) {
			return true;
		}
		if (!target.isSubRow) {
			return false;
		}
		const parent = findParent(targets, index);
		return !!parent && settingMatchesQuery(parent, needle);
	});
}

/** How many rows match the search in each group. Groups with no matches are omitted. */
export function countMatchesByCategory(targets: ISettingSearchTarget[], query?: string): Map<SettingCategory, number> {
	const counts = new Map<SettingCategory, number>();
	for (const target of filterSettings(targets, { query })) {
		counts.set(target.category, (counts.get(target.category) ?? 0) + 1);
	}
	return counts;
}
